import { SysBase, SysModel } from '../api'
import { EntryModel } from '@Protium/vue-ozone-utils'
import { UUID } from '@Protium/vue-ozone-utils/types/utils'
import { EAN, SKU } from './product'
import { Fulfillment, OrderDeliveryInfo, OrderProduct, OrderProductId, OrderStatusInfo, ShipmentLabel, SourceOrderProduct } from './order'
import { Status } from '../status'
import { OrderType, DraftType } from '../types'
import { CatalogueFulfillmentTiers, CatalogueServiceConfig } from './catalogue'
import { Address, Contact, Dimensions, UniqKey } from '../generics'
import { AccountingDetails, CompanyRegistration, Financial, InvoiceMeta, Tax } from './company-details'
import { ImportInputData } from './import'
import { ContentType, Timerange } from '@/services/directr'
import { Primitive } from 'd3'

export enum CategoryType {
  Product = 'product',
  Stock = 'stock',
  Image = 'image',
  Packing = 'packing',
  Any = 'any'
}

export enum TaskType {
  Pack = 'pack',
  Pick = 'pick',
  Move = 'move',
  Receive = 'receive',
  Default = 'default',
  Client = 'client',
  Operation = 'operation',
  Order_Issue = 'order_issue',
  Order_Draft = 'order_draft',
  Store = 'store'
}

export interface EntryState<T> {
  items: Array<T>
  model: EntryModel
  multi: boolean
  selection: Array<T>
  active: T | Array<T> | null
  loading: boolean
}

export interface BaseService {
  id: string
  type: string
  name?: string
}

export interface BaseEntry {
  sys: SysModel
}

export interface CtDevice extends BaseService {
  status?: Array<string>
}

export interface CtTask extends BaseEntry {
  readonly id: UniqKey,
  reference: string,
  status: Array<Status>,
  type: TaskType,
  items: Array<UniqKey> | Array<Record<string, any>>, // UUID's when retrieved from api, can be transformed / resolved into objects
  notes?: string,
  tags?: Array<string>,
  batch_id?: string,
  meta?: Record<string, any>,
  selection?: Record<string, any>
  // local?: boolean // ui-only boolean for denoting a task is local-only. (also prevents the task from being saved on accident without conversion first)
}

export interface CtCategory extends BaseEntry {
  name: string,
  label?: string,
  parent?: UUID,
  type?: CategoryType
}

export interface CtCatalogue extends BaseEntry {
  id: number
  name: string
  client: UUID | null,
  aliases?: Array<string>
  map?: UUID, // datamap ref
  print_slip?: boolean
  packing_slip?: Record<string, any>
  shipping_time?: string
  shipping_carrier?: string
  sku_append?: string
  sku_separator?: string
  sku_prepend?: string
  fix_fulfillment_tiers?: boolean // will always ignore order-set packing data when calculating invoice
  inventory_prices?: { [key: CtCategory['sys']['id']]: string | number }
  invoice_inventory?: boolean
  service?: CatalogueServiceConfig
  fulfillment_prices: CatalogueFulfillmentTiers
  token?: { access_token?: string },
  products?: Array<UUID> // will be deprecated when products are moved to catalogue_products table
}

export interface CtSupplierCatalogue extends BaseEntry {
  name: string
  tags: Array<string>
  production_time: number
  shipping_time: number
  supplier: UUID
  service?: CatalogueServiceConfig
}

export interface CtPacking extends BaseEntry {
  id: string
  name?: string
  dimensions?: Dimensions
  products?: Array<any>
  weight: number
  description?: string
  categories?: Array<UUID>
  draft?: boolean // used as a ui editing flag
}

export interface BaseProduct extends BaseEntry {
  title: string
  sku?: SKU
  categories?: Array<UUID>
  description?: string
  variants?: Array<UUID>
}

export interface CtStockItem extends BaseProduct {
  ean?: Array<EAN>
  packing: Array<UUID> | Array<CtPacking>
  images?: Array<UUID>
  vendor?: string
  brand?: string
  tax_tier?: string,
  vat?: number
  weight?: number
  type?: string
  meta?: Record<string, any>
  price?: number
  batch?: number
  props?: Record<CtProp['sys']['id'], string | number>
  producer?: string
  minimum_order_quantity?: number
  supply_chain_tracking?: boolean,
  dimensions?: Dimensions
  volume?: number
}
export interface CtProduct extends BaseProduct {
  ean?: Array<EAN>
  image?: string
  images: Array<UUID>
  fixed_fulfillment_tier?: Array<UUID> | Array<CtPacking>,
  components: Array<UUID> | Array<CtStockItem>,
  options?: Array<CtProp>
}

export interface CatalogueBaseProduct extends BaseEntry {
  id: UUID
  catalogue: UUID
  title?: string
  active?: boolean
  meta?: Record<string, any>
  // images?: Array<string>
}
export interface CtCatalogueProduct extends CatalogueBaseProduct {
  price?: number,
  images?: Array<UUID>,
  sku?: number
}

export interface StockItemPrice {
  id: UUID, // identifies the option in orders & across updates
  label?: string, // optional name
  value: string | number, // the price value array in the price_list
  // NOTE when updating catalogue_stock_item, this is treated as unique. the incomming price_list value with the same quantity will only update the label &
  quantity: number, // item quantity
  active?: boolean // if the price should be available for ordering, if not set, should be treated as if "true"
}
export interface CtCatalogueStockItem extends CatalogueBaseProduct {
  supplier_product_id?: string
  description?: string
  tax_tier?: string
  deliverable_from?: Date
  deliverable_to?: Date,
  supply_chain_tracking_id?: string
  images?: Array<UUID>,
  price_list?: Array<StockItemPrice>,
  price_lock?: boolean // if the price can be updated by the supplier via the supplier-api
}

export interface CtStockLedger extends BaseEntry {
  id: UUID
  count: number
  catalogue?: string
  tags?: Array<string>
}

export interface LocationBufferItem {
  batch: string
  count: number
  reference: string
  transiting: number
  selected: number
}

export interface CtStorageLocation extends BaseEntry {
  id?: string
  description?: string
  /** @deprecated */
  inventory_locations?: Array<UUID>
  sub_locations?: Array<UUID>,
  buffer?: Record<UUID, LocationBufferItem[]>
}

export interface CtStorageType extends BaseEntry {
  id: string,
  dimensions?: Dimensions,
  volume?: number,
  material?: string,
}

export interface CtInventoryLocation<C = UUID, S = UUID, ST = UUID, SL = UUID> extends BaseEntry {
  id: string
  name?: string
  catalogues?:  Array<C> | Array<CtCatalogue>
  storage_type?: ST | CtStorageType
  storage_location?: SL | CtStorageLocation
  description?: string
  capacity?: number
  stock_item?: S | CtStockItem
  transiting?: number
  count?: number
  notes?: string
}

export enum ComponentPropType {
  Default = 'default'
}
export interface CtProp extends BaseEntry {
  id: string,
  value: string,
  name?: string,
  type?: ComponentPropType
}

export type Bin = { name?: string, dimensions: any, id: string, weight: number, products: Array<{ id: string, quantity: number, status?: Status }>, components: Array<{ id: string | UUID, parent: string, quantity: number, sku?: string, ean?: Array<string> }> }
export type PackingItem = Omit<Bin, 'components'> & { components: Array<{ id: string | UUID, parent: Partial<OrderProduct> & {id:string}, quantity: number, selected?: boolean } & Required<OrderProduct>['components'][number]> }

export interface CtOrder<P extends Partial<OrderProduct> = OrderProduct> extends BaseEntry {
  id: string
  status: Array<Status>
  catalogue: UUID | CtCatalogue
  shipping_address: Address
  type: OrderType
  products: Array<P>

  reference?: string
  delivery?: OrderDeliveryInfo
  billing_address?: Address
  firstname?: string
  lastname?: string
  fullname?: string
  packing?: Array<Bin>
  service?: string
  price?: number
  status_info?: OrderStatusInfo
  shipments?: Array<ShipmentLabel>
  fulfillments?: Array<Fulfillment>
  operational_notes?: string
  notes?: string
  tags?: Array<string>
  raw?: Record<string, any >
  meta?: Record<string, any>
  invoiced?: boolean
  batch_id?: string
  timeregister_picking_start?: Date | number | null
  timeregister_picking_end?: Date | number | null
  timeregister_packing_start?: Date | number | null
  timeregister_packing_end?: Date | number | null
}

export interface CtDraft extends BaseEntry {
  id: string
  service: string
  status: Array<Status>
  type: DraftType

  status_info?: OrderStatusInfo
  raw?: Record<string, any>
  batch_id?: string
}


export interface CtSourceOrder extends BaseEntry {
  service?: string
  batch_id?: string
  id: number
  reference?: string
  status: Array<Status>
  supplier: UUID | CtSupplier
  catalogue: UUID | CtCatalogue
  storage_location: UUID | CtStorageLocation,
  shipping_address: Address // delivery address
  billing_address?: Address
  type: OrderType
  products: Array<SourceOrderProduct>
  price?: number
  status_info?: OrderStatusInfo
  shipments?: Array<ShipmentLabel>
  fulfillments?: Array<Fulfillment>
  operational_notes?: string
  notes?: string
  tags?: Array<string>
  meta?: Record<string, any>
  invoiced?: boolean
}

export interface InvoiceItem {
  [key:string]: any
}
export interface CtInvoice extends BaseEntry {
  id: number
  name?: string

  client: UUID,
  catalogue: UUID,

  sent?: boolean,
  paid?: boolean,
  type?: 'default' | 'transiting'

  timerange_start: string
  timerange_end: string
  expires_at: string
  items: Array<InvoiceItem>
  orders: Array<string>
  headers: Record<string, any>
  reset: boolean
}

export interface CtClient extends BaseEntry {
  id?: string,
  name: string,
  logo_uri?: string,
  tax?: Record<Tax, any>,
  company_name?: string,
  company_registration?: Record<CompanyRegistration, any>,
  company_address?: Address,
  billing_address?: Address,
  contact?: Contact,
  financial?: Record<Financial, any>,
  invoice_meta?: Record<InvoiceMeta, any>,
  accounting_details?: Record<AccountingDetails, any>
}
export interface CtSupplier extends BaseEntry {
  id?: string,
  name: string,
  logo_uri?: string,
  tax?: Record<Tax, any>,
  company_name?: string,
  company_registration?: Record<CompanyRegistration, any>,
  company_address?: Address,
  billing_address?: Address,
  contact?: Contact,
  financial?: Record<Financial, any>,
  invoice_meta?: Record<InvoiceMeta, any>,
  accounting_details?: Record<AccountingDetails, any>

  users: Array<UUID>
  token: Record<string, any>
  tags: Array<string>
}
export interface CtDatamap extends BaseEntry {
  name?: string,
  type: string,
  description?: string,
  map?: Record<string, any>
}

export interface CtImport extends BaseEntry {
  id: string,
  refs: Array<string>,
  type: string,
  reference_field: string,
  input_data: ImportInputData,
  datamap: UUID,
  status: OrderStatusInfo
}

export interface CtSource extends BaseEntry {
  id?: string
  status?: Array<string>
  allow?: string
  data?: any
  history?: any
  meta?: any
}

export interface CtAsset extends BaseEntry {
  file?: string,
  mimetype?: string,
  file_name?: string,
  service?: string
}

export enum EventType {
  Success = 'success',
  Info = 'info',
  Warn = 'warn',
  Error = 'error'
}
export interface CtEvent extends BaseEntry {
  reference: UUID,
  content_type: 'orders' | ContentType.Order | ContentType.SourceOrder | ContentType.Product | ContentType.Import | ContentType.Invoice | ContentType.CatalogueProduct | ContentType.CatalogueStockItem,
  type: EventType,
  label: string,
  description: string,
  meta: Record<string, Primitive>
}



export interface CtShipmentCarrier extends BaseEntry {
  service: string
  name: string
  process_initial?: boolean
  group?: UUID
}

export interface CtShipmentCarrierGroup extends BaseEntry {
  name: string
  process_initial?: boolean
  process_order?: Array<UUID>

  // -- api util field
  shipment_carriers?: Array<UUID>
}

// NOTE used for warehouse import (previews)

export type ImportableRef<T = Record<string, string | number | Array<string | number> | Record<string, any>>> = Array<{
  id: string,
  entry?: UUID
  data?: ImportableItem | Record<string, string | number | Array<string | number> | Record<string, any>>
} & T>

type ImportableItemRefKey = ContentType.InventoryLocation | ContentType.Catalogue | ContentType.CatalogueStockItem | ContentType.Packing | ContentType.Prop | 'variants'
export interface ImportableItemRefs {
  [ContentType.InventoryLocation]?: ImportableRef<{
    storage_location: string
    count: number
    locations?: Array<string>
    catalogues?: Array<string>
  }>

  [ContentType.Catalogue]?: ImportableRef<{
    client?: string
    price?: string
    title?: string
  }>

  [ContentType.CatalogueStockItem]?: ImportableRef<{
    // price?: string | Array<StockItemPrice>
    title?: string
    price?: string
    quantity?: number
  }>

  [ContentType.Packing]?: ImportableRef<{
    id?: string
    type?: string
  }>

  [ContentType.Prop]?: ImportableRef<{
    name?: string
    type?: string
    value?: string | number
  }>

  // variants: ImportableRef | Array<ImportableItem>
  variants?: ImportableRef
}

export enum ImportableCsvKey {
  title = 'title',
  ean = 'ean',
  sku = 'sku',
  description = 'description',
  categories = 'categories',
  reference = 'reference',
  hs = 'hs',
  brand = 'brand',
  // vendor = 'vendor',
  producer = 'producer',
  batch = 'batch',
  minimum_order_quantity = 'minimum_order_quantity',
  length = 'length',
  width = 'width',
  height = 'height',
  unit = 'unit',
  dimensions = '  dimensions',
  volume = 'volume',
  weight = 'weight',
  meta = 'meta',
  images = 'images',
  clients = 'clients',
  warehouse = 'warehouse',
  location = 'location',
  location_count = 'location_count',
  storage_packing = 'storage_packing',
  enrichment = 'enrichment',
  channel = 'channel',
  channel_price = 'channel_price',
  channel_title = 'channel_title',
  source = 'source',
  source_price = 'source_price',
  source_quantity = 'source_quantity',
  fulfillment_packing = 'fulfillment_packing',
  fulfillment_packing_type = 'packing_type',
  variants = 'variants',
  refs = 'refs'
}
export interface ImportableItem {
  title: string
  ean: Array<string>
  sku: string

  description?: string
  categories?: Array<string>

  reference?: string // article number
  hs?: string

  brand?: string
  // vendor?: string
  producer?: string

  batch?: number
  minimum_order_quantity?: number

  dimensions?: Dimensions
  volume?: number // can be computed from dimensions
  weight?: string

  meta?: Record<string, string | number>

  images?: Array<string> // URLs

  catalogues?: Array<string>
  clients?: Array<string>

  // enrichment data ---------------------------
  enrichment?: Record<ContentType | 'variants', Array<UUID> | UUID>

  // -------------------------------------------

  refs?: ImportableItemRefs
}

// TODO: Content-type
// export interface CtProductImage {
//   uuid: UUID,
// }
